import { useEffect, useState } from "react";
import { apiGet, getAccessToken, getTenantSubdomain } from "../services/api";
import type { Notification } from "../types/models";

interface UseNotificationsReturn {
  notifications: Notification[];
  loading: boolean;
  error: Error | null;
  unreadCount: number;
}

/**
 * Fetches notifications for the given role via REST API and connects
 * to the real-time SSE stream (/api/notifications/stream) for instant updates.
 *
 * Requirements: GAP-002E
 *
 * @param role - The current user's role (or null if unauthenticated)
 * @returns { notifications, loading, error, unreadCount }
 */
export function useNotifications(
  role: string | null,
): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    let eventSource: EventSource | null = null;

    if (!role) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    async function loadNotifications() {
      setLoading(true);
      setError(null);
      try {
        const res = await apiGet<{ notifications?: Notification[]; data?: Notification[] } | Notification[]>("/api/notifications");
        const docs = Array.isArray(res) ? res : res.notifications || res.data || [];
        if (!cancelled) {
          setNotifications(docs.slice(0, 50));
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error("Failed to fetch notifications"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadNotifications();

    // Setup Real-Time Server-Sent Events (SSE) Stream
    try {
      const token = getAccessToken();
      const subdomain = getTenantSubdomain();
      if (token && typeof window !== "undefined" && window.EventSource) {
        const baseUrl = (import.meta.env.VITE_API_BASE_URL as string) || "";
        const cleanBase = baseUrl.endsWith("/api") ? baseUrl.slice(0, -4) : baseUrl;
        const streamUrl = `${cleanBase}/api/notifications/stream?token=${encodeURIComponent(token)}${
          subdomain ? `&subdomain=${encodeURIComponent(subdomain)}` : ""
        }`;

        eventSource = new EventSource(streamUrl);

        eventSource.addEventListener("notification", (event) => {
          if (cancelled) return;
          try {
            const newNotif = JSON.parse(event.data);
            setNotifications((prev) => {
              if (prev.some((n) => n.id === newNotif.id)) return prev;
              return [newNotif, ...prev];
            });
          } catch (e) {
            console.error("[useNotifications] Failed to parse SSE event data:", e);
          }
        });

        eventSource.onerror = () => {
          // EventSource automatically handles reconnects
        };
      }
    } catch (sseErr) {
      console.warn("[useNotifications] Failed to initialize SSE stream:", sseErr);
    }

    return () => {
      cancelled = true;
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [role]);

  return {
    notifications,
    loading,
    error,
    unreadCount: notifications.filter((n) => !n.isRead).length,
  };
}
